import React, { useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Chart from "react-apexcharts";
import { motion } from "framer-motion";
import { FiTrendingUp, FiClipboard, FiFileText, FiShield } from "react-icons/fi";
import { toast } from "react-toastify";

import KPICard, { DashboardCard } from "../../Common/KPICard";
import SelectFilter from "../../Common/SelectFilter";
import DateRangeFilter from "../../../Common/DateFilter";
import ActionButton from "../../Common/ActionButton";
import StatusChip from "../../Common/StatusChip";
import "../../../dashboardLayout.css";

const theme = {
  primary: "#0b6462",
  primaryLight: "#eefbfb",
  secondary: "#64748b",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
  info: "#38bdf8",
  textPrimary: "#1e293b",
  textSecondary: "#64748b",
  border: "#e2e8f0",
  surface: "#ffffff",
  shadow: "0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)",
  shadowHover: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { y: 15, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 110 } },
};

const PAYABLE_AGING = [
  { label: "0–7 Days (In Process)", amount: "PKR 4.2M", value: 4.2, max: 9.4, color: "#22c55e" },
  { label: "8–15 Days", amount: "PKR 6.8M", value: 6.8, max: 9.4, color: "#38bdf8" },
  { label: "16–30 Days", amount: "PKR 9.4M", value: 9.4, max: 9.4, color: "#f59e0b" },
  { label: "Over 30 Days", amount: "PKR 2.3M", value: 2.3, max: 9.4, color: "#f87171" },
];

const RECENT_RUNNING_BILLS = [
  {
    id: 1,
    billNo: "RAB-2026-0098",
    contractor: "Haji Aslam & Sons",
    workOrderNo: "WO-2026-0045",
    grossCertified: "PKR 1.40M",
    netPayable: "PKR 1.12M",
    status: "Paid / Settled",
    statusType: "success",
  },
  {
    id: 2,
    billNo: "RAB-2026-0099",
    contractor: "Malik Electrics",
    workOrderNo: "WO-2026-0052",
    grossCertified: "PKR 0.85M",
    netPayable: "PKR 0.62M",
    status: "Approved",
    statusType: "info",
  },
  {
    id: 3,
    billNo: "RAB-2026-0100",
    contractor: "Bilal Steel Co.",
    workOrderNo: "WO-2026-0058",
    grossCertified: "PKR 1.20M",
    netPayable: "PKR 0.95M",
    status: "Under Review",
    statusType: "warning",
  },
  {
    id: 4,
    billNo: "RAB-2026-0101",
    contractor: "Khan Concrete",
    workOrderNo: "WO-2026-0048",
    grossCertified: "PKR 3.50M",
    netPayable: "PKR 2.80M",
    status: "Draft",
    statusType: "default",
  },
];

const WorksDashboard = () => {
  const history = useHistory();
  const [selectedProject, setSelectedProject] = useState("");
  const [filterDates, setFilterDates] = useState({ from: "", to: "" });

  const kpis = [
    {
      title: "Total Subcontracted value",
      value: "PKR 284.60M",
      subtext: "+8.5% from last month",
      subtextColor: "#16a34a",
      icon: FiTrendingUp,
      color: "#0b6462",
      onClick: null,
    },
    {
      title: "Work Certified (MBs) to Date",
      value: "PKR 112.40M",
      subtext: "39.5% work done across 14 contractors",
      subtextColor: "#64748b",
      icon: FiClipboard,
      color: "#0284c7",
      onClick: () => history.push("/works/measurement-book"),
    },
    {
      title: "Contractor Running Bills (RAs)",
      value: "PKR 98.70M",
      subtext: "PKR 13.70M MB record work unbilled",
      subtextColor: "#64748b",
      icon: FiFileText,
      color: "#d97706",
      onClick: () => history.push("/works/contractor-bills"),
    },
    {
      title: "Overall Retention Held (Outward)",
      value: "PKR 14.23M",
      subtext: "Withheld from progressive RA bills",
      subtextColor: "#64748b",
      icon: FiShield,
      color: "#7c3aed",
      onClick: null,
    },
  ];

  // Grouped Column Bar Chart: Contract Value vs. Certified Work vs. Billed
  const barChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          borderRadius: 4,
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val}M`,
        offsetY: -20,
        style: {
          fontSize: "11px",
          fontWeight: 700,
          colors: ["#334155"],
        },
      },
      colors: ["#0b6462", "#38bdf8", "#d97706"],
      xaxis: {
        categories: [
          "Haji Aslam & Sons",
          "Khan Concrete Works",
          "Malik Electrics",
          "Bilal Steel Co.",
        ],
        labels: {
          style: {
            fontSize: "12px",
            fontWeight: 600,
            colors: "#334155",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          formatter: (val) => `PKR ${val}M`,
          style: {
            fontSize: "11px",
            colors: "#64748b",
          },
        },
      },
      grid: {
        borderColor: "#f1f5f9",
        strokeDashArray: 3,
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        fontSize: "12px",
        markers: { radius: 12 },
        itemMargin: { horizontal: 10, vertical: 0 },
      },
      tooltip: {
        theme: "light",
        y: {
          formatter: (val) => `PKR ${val}M`,
        },
      },
    }),
    [],
  );

  const barChartSeries = [
    {
      name: "Contract Value",
      data: [45.5, 35.0, 18.5, 15.0],
    },
    {
      name: "Certified Work",
      data: [18.2, 25.0, 6.2, 9.7],
    },
    {
      name: "Billed",
      data: [15.1, 23.5, 5.0, 8.4],
    },
  ];

  // Donut Chart: Retention & Mobilization Advances
  const donutChartOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        fontFamily: "inherit",
        toolbar: { show: false },
      },
      labels: ["Mobilization Advance Balance", "Retention Held (Accumulated)"],
      colors: ["#2563eb", "#d97706"],
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Number(val).toFixed(1)}%`,
        style: {
          fontSize: "12px",
          fontWeight: "700",
          colors: ["#ffffff"],
        },
        dropShadow: { enabled: false },
      },
      stroke: {
        width: 3,
        colors: ["#ffffff"],
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: false,
            },
          },
        },
      },
      tooltip: {
        theme: "light",
        y: {
          formatter: (val) => `PKR ${val}M`,
        },
      },
    }),
    [],
  );

  const donutChartSeries = [11.2, 8.0];

  const handleDateRangeChange = useCallback((range) => {
    setFilterDates(range);
  }, []);

  const handleGenerateReport = () => {
    const rangeText =
      filterDates.from && filterDates.to ? ` for ${filterDates.from} to ${filterDates.to}` : "";
    toast.info(`Generating Subcontracting Works Summary Report${rangeText}...`);
  };

  return (
    <div className="pt-2 app_font pb-5">
      {/* ── Filter Toolbar ── */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-between mb-3 app_font"
        style={{ gap: "12px" }}
      >
        <div>
          <h4
            style={{
              margin: 0,
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            Outward Works (Subcontracting) Dashboard
          </h4>
        </div>

        <div className="d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
          <SelectFilter
            id="worksProjectFilter"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            label=""
            defaultOptionLabel="All Active Projects"
            maxWidth="240px"
            options={[
              { value: "Gulberg Heights", label: "Gulberg Heights Commercial" },
              { value: "ADF Colony", label: "ADF Colony Block C" },
              { value: "Giga Mall Wing B", label: "Giga Mall Wing B" },
              { value: "River View Suites", label: "River View Suites" },
            ]}
          />

          <div className="d-flex align-items-center" style={{ width: "230px" }}>
            <div className="floating-field" style={{ width: "100%" }}>
              <DateRangeFilter onChange={handleDateRangeChange} />
              <label>Date Range</label>
            </div>
          </div>

          <ActionButton text="Generate Report" variant="save" onClick={handleGenerateReport} />
        </div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="erp-dashboard-wrap"
      >
        {/* ── Row 1: KPI Summary Cards ── */}
        <div className="erp-kpi-grid">
          {kpis.map((kpi, idx) => (
            <div
              key={idx}
              onClick={kpi.onClick}
              style={{ cursor: kpi.onClick ? "pointer" : "default" }}
            >
              <KPICard
                title={kpi.title}
                value={kpi.value}
                subtitle={kpi.subtext}
                icon={kpi.icon}
                themeColor={kpi.color}
                themeBgColor={`${kpi.color}15`}
                subtitleColor={kpi.subtextColor}
                itemVariants={itemVariants}
              />
            </div>
          ))}
        </div>

        {/* ── Row 2: Contract Value vs Certified vs Billed & Retention / Advances ── */}
        <div className="erp-two-col-grid">
          {/* Left: Grouped Bar Chart */}
          <DashboardCard
            title="Contract Value vs. Certified Work vs. Billed"
            subtitle="Compared across outward contractors/contracts"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div style={{ minHeight: "280px" }}>
              <Chart options={barChartOptions} series={barChartSeries} type="bar" height={280} />
            </div>
          </DashboardCard>

          {/* Right: Retention & Mobilization Advances Donut */}
          <DashboardCard
            title="Retention & Mobilization Advances"
            subtitle="Balance of funds out with subcontractors"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div
              className="d-flex align-items-center justify-content-between h-100 py-2 flex-wrap"
              style={{ minHeight: "280px", gap: "10px" }}
            >
              {/* Left Legend / Metric Box */}
              <div
                style={{
                  flex: "1 1 120px",
                  textAlign: "left",
                  padding: "12px 14px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div className="d-flex align-items-center mb-1" style={{ gap: "6px" }}>
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "2px",
                      background: "#2563eb",
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>
                    Mobilization Advance
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#0f172a",
                    paddingLeft: "16px",
                  }}
                >
                  PKR 11.2M
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#2563eb",
                    fontWeight: 600,
                    paddingLeft: "16px",
                  }}
                >
                  58.4% of total
                </div>
              </div>

              {/* Center Donut */}
              <div
                style={{
                  width: "240px",
                  height: "240px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                }}
              >
                <Chart
                  options={donutChartOptions}
                  series={donutChartSeries}
                  type="donut"
                  height={240}
                  width={240}
                />
              </div>

              {/* Right Legend / Metric Box */}
              <div
                style={{
                  flex: "1 1 120px",
                  textAlign: "left",
                  padding: "12px 14px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div className="d-flex align-items-center mb-1" style={{ gap: "6px" }}>
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "2px",
                      background: "#d97706",
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>
                    Retention Held
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#0f172a",
                    paddingLeft: "16px",
                  }}
                >
                  PKR 8.0M
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#d97706",
                    fontWeight: 600,
                    paddingLeft: "16px",
                  }}
                >
                  41.6% of total
                </div>
              </div>
            </div>
          </DashboardCard>
        </div>

        {/* ── Row 3: Net Payable & Billing Ageing + Recent Running Bills ── */}
        <div className="erp-two-col-grid erp-mb-0">
          {/* Left: Net Payable & Billing Ageing */}
          <DashboardCard
            title="Net Payable & Billing Ageing"
            subtitle="Payables to thekedars"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div
              className="d-flex flex-column justify-content-around h-100 py-2"
              style={{ gap: "18px", minHeight: "260px" }}
            >
              {PAYABLE_AGING.map((item, idx) => {
                const pct = Math.round((item.value / item.max) * 100);
                return (
                  <div key={idx} className="d-flex align-items-center">
                    <div
                      style={{
                        width: "160px",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "#334155",
                        textAlign: "right",
                        paddingRight: "14px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={item.label}
                    >
                      {item.label}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{
                        height: "22px",
                        background: "#f1f5f9",
                        borderRadius: "6px",
                        overflow: "hidden",
                      }}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: idx * 0.1 }}
                        style={{
                          height: "100%",
                          background: item.color,
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: "95px",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        color: "#0f172a",
                        paddingLeft: "14px",
                        textAlign: "right",
                      }}
                    >
                      {item.amount}
                    </div>
                  </div>
                );
              })}
            </div>
          </DashboardCard>

          {/* Right: Recent Subcontractor Running Bills (RA) */}
          <DashboardCard
            title="Recent Subcontractor Running Bills (RA)"
            subtitle="Latest RA bills submitted for verification & payment"
            theme={theme}
            itemVariants={itemVariants}
            action={
              <button
                type="button"
                className="btn btn-sm btn-link p-0"
                style={{
                  fontSize: "12px",
                  color: "var(--color-primary, #0b6462)",
                  fontWeight: 600,
                }}
                onClick={() => history.push("/works/contractor-bills")}
              >
                View All Bills
              </button>
            }
          >
            <div className="table-responsive" style={{ minHeight: "240px" }}>
              <table className="table table-hover align-middle mb-0" style={{ fontSize: "12.5px" }}>
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      color: "#64748b",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Bill / RA No.</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Contractor</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Work Order No.</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Gross Certified</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Net Payable</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600, textAlign: "center" }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {RECENT_RUNNING_BILLS.map((bill) => (
                    <tr
                      key={bill.id}
                      style={{ cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                      onClick={() => history.push("/works/contractor-bills")}
                    >
                      <td
                        style={{
                          padding: "10px",
                          fontWeight: 600,
                          color: "var(--color-primary, #0b6462)",
                        }}
                      >
                        {bill.billNo}
                      </td>
                      <td style={{ padding: "10px", fontWeight: 500, color: "#1e293b" }}>
                        {bill.contractor}
                      </td>
                      <td style={{ padding: "10px", color: "#64748b" }}>{bill.workOrderNo}</td>
                      <td style={{ padding: "10px", color: "#475569" }}>{bill.grossCertified}</td>
                      <td style={{ padding: "10px", fontWeight: 600, color: "#0f172a" }}>
                        {bill.netPayable}
                      </td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        <StatusChip status={bill.status} type={bill.statusType} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DashboardCard>
        </div>
      </motion.div>
    </div>
  );
};

export default WorksDashboard;
